import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Select,
  Input,
  InputNumber,
  message,
  Empty,
} from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import CustomLayout from "../../Layout/CustomLayout";
import PharmacySideMenu from "../../Layout/CustomLayout/PharmacySideMenu";
import { UiSectionCard, UiButton, UiTag, ActionIconButton } from "../../ui";
import AsyncSearchSelect from "../common/AsyncSearchSelect";
import { useOrgFacilities } from "../common/useOrgFacilities";
import { fetchActivePharmacyMedicines } from "../common/medicineSearch";
import { getPharmacyMedicineBatchesApi } from "../../../services/pharmacyMedicines.services";
import { createPharmacyTransferApi } from "../../../services/pharmacyTransfers.services";
import { getErrorMessage } from "../../../helpers/error";
import { formatMoney } from "../common/money";
import styles from "./StockTransfer.module.css";
import PharmacyGlobalSearch from "../common/PharmacyGlobalSearch";

const { TextArea } = Input;

let rowSeq = 0;
const newRow = () => ({
  key: `row-${(rowSeq += 1)}`,
  medicine: undefined,
  medicineName: "",
  batch: undefined,
  quantity: null,
  batches: [],
  loadingBatches: false,
});

const StockTransferPage = () => {
  const navigate = useNavigate();
  const {
    destinations: facilityOptions,
    currentFacilityName,
    facilityCountKnown,
    loading: loadingFacilities,
  } = useOrgFacilities();

  const [toFacility, setToFacility] = useState(undefined);
  const [remarks, setRemarks] = useState("");
  const [rows, setRows] = useState([newRow()]);
  const [submitting, setSubmitting] = useState(false);

  const patchRow = (key, patch) =>
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  const handleMedicineSelect = (key, medicineId, option) => {
    patchRow(key, {
      medicine: medicineId,
      medicineName: option?.label || "",
      batch: undefined,
      quantity: null,
      batches: [],
      loadingBatches: Boolean(medicineId),
    });
    if (!medicineId) return;

    getPharmacyMedicineBatchesApi(medicineId)
      .then((res) => {
        const list = res?.data?.results ?? res?.data ?? [];
        patchRow(key, {
          batches: Array.isArray(list) ? list : [],
          loadingBatches: false,
        });
      })
      .catch(() => patchRow(key, { batches: [], loadingBatches: false }));
  };

  const selectedBatch = (row) =>
    row.batches.find((b) => b.id === row.batch) || null;

  /** The API validates this too, but catching it here avoids a round trip. */
  const rowError = (row) => {
    if (!row.medicine || !row.batch) return null;
    const batch = selectedBatch(row);
    const available = Number(batch?.available_qty ?? 0);
    const qty = Number(row.quantity ?? 0);
    if (!qty) return null;
    if (qty > available) return `Only ${available} available in this batch`;
    return null;
  };

  const validRows = rows.filter(
    (r) => r.medicine && r.batch && Number(r.quantity) > 0 && !rowError(r)
  );
  const totalQuantity = validRows.reduce(
    (sum, r) => sum + Number(r.quantity || 0),
    0
  );

  /*
   * Two figures, because they answer different questions: purchase value is
   * what the stock cost the sending facility (the amount actually moving off
   * its books), MRP value is what it could be sold for at the receiver.
   */
  const totals = validRows.reduce(
    (acc, r) => {
      const batch = selectedBatch(r);
      const qty = Number(r.quantity || 0);
      acc.purchase += qty * Number(batch?.purchase_rate || 0);
      acc.mrp += qty * Number(batch?.mrp || 0);
      return acc;
    },
    { purchase: 0, mrp: 0 }
  );

  const canSubmit =
    Boolean(toFacility) && validRows.length > 0 && validRows.length === rows.filter((r) => r.medicine).length;

  const handleSubmit = () => {
    if (!toFacility) {
      message.error("Select the facility to transfer to.");
      return;
    }
    if (!validRows.length) {
      message.error("Add at least one medicine with a batch and quantity.");
      return;
    }

    setSubmitting(true);
    createPharmacyTransferApi({
      to_facility: toFacility,
      remarks: remarks || undefined,
      items: validRows.map((r) => ({
        batch: r.batch,
        quantity: Number(r.quantity),
      })),
    })
      .then((res) => {
        message.success(
          res?.data?.transfer_number
            ? `Transfer ${res.data.transfer_number} requested.`
            : "Transfer requested."
        );
        navigate("/pharmacy/medicines");
      })
      .catch((err) => message.error(getErrorMessage(err)))
      .finally(() => setSubmitting(false));
  };

  // Only block when we positively know there is nowhere to send stock.
  if (!loadingFacilities && facilityCountKnown && facilityOptions.length === 0) {
    return (
      <CustomLayout
      headerSearch={<PharmacyGlobalSearch />}
        layoutClassName="reception-layout compact-footer-layout pharmacy-motion"
        sideContent={<PharmacySideMenu />}
        header={
        // The mockup's header carries only search and the account menu; each
        // screen names itself in its own heading. Kept truthy so CustomLayout
        // still renders the bar.
        <></>
      }
      >
        <UiSectionCard>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <div>
                <div className={styles.emptyTitle}>
                  Stock transfer needs a second facility
                </div>
                <div className={styles.emptyBody}>
                  This organisation has only {currentFacilityName}. Stock can
                  only be transferred between facilities in the same
                  organisation.
                </div>
              </div>
            }
          >
            <UiButton onClick={() => navigate("/pharmacy/medicines")}>
              Back to Medicines
            </UiButton>
          </Empty>
        </UiSectionCard>
      </CustomLayout>
    );
  }

  return (
    <CustomLayout
      layoutClassName="reception-layout compact-footer-layout pharmacy-motion"
      sideContent={<PharmacySideMenu />}
      header={
        // The mockup's header carries only search and the account menu; each
        // screen names itself in its own heading. Kept truthy so CustomLayout
        // still renders the bar.
        <></>
      }
      footer={
        <div className={styles.footerActions}>
          <UiButton
            variant="secondary"
            onClick={() => navigate("/pharmacy/medicines")}
          >
            Cancel
          </UiButton>
          <UiButton
            loading={submitting}
            disabled={!canSubmit}
            onClick={handleSubmit}
          >
            Request Transfer
          </UiButton>
        </div>
      }
    >

      <UiSectionCard title="Facility Details">
        <div className={styles.facilityGrid}>
          <div className={styles.field}>
            <label className={styles.label}>From</label>
            <Input value={currentFacilityName} disabled />
          </div>

          <div className={styles.field}>
            <label className={styles.label}>
              <span className={styles.required}>*</span> Transfer To
            </label>
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Select facility"
              value={toFacility}
              onChange={setToFacility}
              options={facilityOptions}
              loading={loadingFacilities}
              notFoundContent={
                loadingFacilities
                  ? "Loading facilities..."
                  : "No other facility in this organisation"
              }
              style={{ width: "100%" }}
            />
          </div>

          <div className={styles.fieldWide}>
            <label className={styles.label}>Remarks</label>
            <TextArea
              rows={1}
              placeholder="Optional note for the receiving facility"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
            />
          </div>
        </div>
      </UiSectionCard>

      <UiSectionCard
        title="Medicines"
        extra={
          <UiButton onClick={() => setRows((prev) => [...prev, newRow()])}>
            + Add Medicine
          </UiButton>
        }
      >
        {!rows.length ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No medicines added"
          />
        ) : (
          <div className={styles.tableScroll}>
            <table className={styles.table}>
              <thead>
                <tr className={styles.headRow}>
                  <th className={styles.cell} style={{ width: "30%" }}>
                    Medicine
                  </th>
                  <th className={styles.cell} style={{ width: "24%" }}>
                    Batch
                  </th>
                  <th className={styles.cellRight} style={{ width: "11%" }}>
                    MRP
                  </th>
                  <th className={styles.cellRight} style={{ width: "13%" }}>
                    Purchase Rate
                  </th>
                  <th className={styles.cell} style={{ width: "14%" }}>
                    Quantity
                  </th>
                  <th className={styles.cell} style={{ width: "8%" }} />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const batch = selectedBatch(row);
                  const error = rowError(row);
                  return (
                    <tr key={row.key} className={styles.bodyRow}>
                      <td className={styles.cell}>
                        <AsyncSearchSelect
                          fetcher={fetchActivePharmacyMedicines}
                          placeholder="Search medicine"
                          value={row.medicine}
                          onChange={(value, option) =>
                            handleMedicineSelect(row.key, value, option)
                          }
                        />
                      </td>
                      <td className={styles.cell}>
                        <Select
                          placeholder={
                            row.loadingBatches ? "Loading..." : "Select batch"
                          }
                          value={row.batch}
                          loading={row.loadingBatches}
                          disabled={!row.medicine || row.loadingBatches}
                          onChange={(value) =>
                            patchRow(row.key, { batch: value, quantity: null })
                          }
                          options={row.batches.map((b) => ({
                            value: b.id,
                            label: `${b.batch_number} · Qty ${b.available_qty}`,
                          }))}
                          notFoundContent="No stock in this facility"
                          style={{ width: "100%" }}
                        />
                        {batch ? (
                          <div className={styles.hint}>
                            Available {batch.available_qty}
                          </div>
                        ) : null}
                      </td>
                      <td className={styles.cellRight}>
                        {batch ? formatMoney(batch.mrp || 0) : "-"}
                      </td>
                      <td className={styles.cellRight}>
                        {batch ? formatMoney(batch.purchase_rate || 0) : "-"}
                      </td>
                      <td className={styles.cell}>
                        <InputNumber
                          min={0}
                          precision={2}
                          disabled={!row.batch}
                          status={error ? "error" : undefined}
                          value={row.quantity}
                          onChange={(value) =>
                            patchRow(row.key, { quantity: value })
                          }
                          style={{ width: "100%" }}
                        />
                        {error ? (
                          <div className={styles.error}>{error}</div>
                        ) : null}
                      </td>
                      <td className={styles.cell}>
                        <ActionIconButton
                          aria-label="Remove row"
                          onClick={() =>
                            setRows((prev) =>
                              prev.filter((r) => r.key !== row.key)
                            )
                          }
                        >
                          <DeleteOutlined />
                        </ActionIconButton>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </UiSectionCard>

      <UiSectionCard title="Transfer Summary">
        <div className={styles.summaryRow}>
          <span>Medicines</span>
          <strong>{validRows.length}</strong>
        </div>
        <div className={styles.summaryRow}>
          <span>Total quantity</span>
          <strong>{totalQuantity}</strong>
        </div>
        <div className={styles.summaryRow}>
          <span>Purchase value</span>
          <strong>{formatMoney(totals.purchase)}</strong>
        </div>
        <div className={styles.summaryRow}>
          <span>MRP value</span>
          <strong>{formatMoney(totals.mrp)}</strong>
        </div>
        <div className={styles.summaryRow}>
          <span>Destination</span>
          <strong>
            {toFacility ? (
              <UiTag tone="info" size="sm">
                {facilityOptions.find((f) => f.value === toFacility)?.label}
              </UiTag>
            ) : (
              "-"
            )}
          </strong>
        </div>

        <p className={styles.note}>
          Requesting a transfer immediately reserves this stock at{" "}
          {currentFacilityName}, so it is no longer sellable here. It is credited
          to the receiving facility only once they confirm receipt.
        </p>

      </UiSectionCard>
    </CustomLayout>
  );
};

export default StockTransferPage;
